/**
 * SEO defaults and per-route meta for landing and public pages.
 * Used by useSEO hook and SEO component for title, description, canonical, and hreflang.
 */
export const siteName = "Kliq CRM";
export const defaultTitle = "Kliq CRM — Intelligent Automation for Modern Teams";
export const defaultDescription =
  "Kliq CRM empowers your team with AI-powered workflows, seamless integrations, and real-time collaboration.";

/** Per-route defaults: path (no trailing slash, root = "/") -> { title, description, keywords? } */
export const routeDefaults = {
  "/": {
    title: "Intelligent Automation for Modern Teams",
    description:
      "Kliq CRM empowers your team with AI-powered workflows, seamless integrations, and real-time collaboration.",
    keywords: "SaaS, automation, workflow, AI, collaboration, productivity",
  },
  "/features": {
    title: "Features",
    description:
      "Discover all the powerful features that make Kliq CRM the complete platform for modern teams.",
    keywords: "features, automation, security, performance, analytics, collaboration",
  },
  "/pricing": {
    title: "Pricing",
    description:
      "Simple, transparent pricing for teams of all sizes. Start free and scale as you grow with Kliq CRM.",
    keywords: "pricing, plans, free trial, enterprise, SaaS",
  },
  "/about": {
    title: "About Us",
    description:
      "Learn about Kliq CRM's mission to empower teams everywhere with intelligent tools that make work more productive, creative, and enjoyable.",
    keywords: "about, company, team, mission, values",
  },
  "/contact": {
    title: "Contact Us",
    description:
      "Get in touch with the Kliq CRM team. We're here to help you with any questions about our platform.",
    keywords: "contact, support, help, Kliq CRM, SaaS",
  },
  "/blog": {
    title: "Blog",
    description:
      "Read the latest articles, tips, and insights from the Kliq CRM team.",
    keywords: "blog, articles, tips, insights, productivity",
  },
  "/integrations": {
    title: "Integrations",
    description:
      "Connect Kliq CRM with your favorite tools and services. Integrate with Slack, GitHub, Stripe, and more.",
    keywords: "integrations, API, webhooks, Slack, GitHub, Stripe",
  },
  "/changelog": {
    title: "Changelog",
    description:
      "Stay up to date with the latest features, improvements, and updates to Kliq CRM.",
    keywords: "changelog, updates, features, release notes",
  },
  "/careers": {
    title: "Careers",
    description:
      "Join the Kliq CRM team. We're building the future of work and looking for talented people to join us.",
    keywords: "careers, jobs, hiring, employment",
  },
  "/documentation": {
    title: "Documentation",
    description:
      "Comprehensive documentation to help you get the most out of Kliq CRM.",
    keywords: "documentation, guides, tutorials, API",
  },
  "/help-center": {
    title: "Help Center",
    description:
      "Find answers to common questions and get help with Kliq CRM.",
    keywords: "help, support, FAQ, questions",
  },
  "/api-reference": {
    title: "API Reference",
    description:
      "Complete API documentation for integrating Kliq CRM into your applications.",
    keywords: "API, documentation, integration, webhooks, SDK",
  },
  "/status": {
    title: "Status",
    description:
      "Check the current status of Kliq CRM services and systems.",
    keywords: "status, uptime, system status, monitoring",
  },
  "/privacy": {
    title: "Privacy Policy",
    description:
      "Learn how Kliq CRM collects, uses, and protects your personal information.",
    keywords: "privacy, policy, data protection, GDPR",
  },
  "/terms": {
    title: "Terms of Service",
    description:
      "Read our terms of service to understand the rules and regulations for using Kliq CRM.",
    keywords: "terms, service, agreement, legal",
  },
  "/security": {
    title: "Security",
    description:
      "Learn about Kliq CRM's security practices and how we protect your data.",
    keywords: "security, encryption, SOC 2, GDPR, compliance",
  },
};

/**
 * Get route defaults for a path (normalizes path to no trailing slash).
 * @param {string} pathname - e.g. "/features" or "/es/features"
 * @returns {{ title: string, description: string, keywords?: string } | undefined}
 */
export function getRouteDefaults(pathname) {
  if (!pathname || pathname === "") return routeDefaults["/"];
  const path = pathname.replace(/\/$/, "") || "/";
  // If path has language prefix (e.g. /es/features), look up by path without prefix
  const withoutLang = path.replace(/^\/[a-z]{2}(-[a-z0-9]+)?(\/|$)/i, "$2" || "/");
  const normalized = withoutLang === "" ? "/" : withoutLang;
  return routeDefaults[normalized];
}

export default {
  siteName,
  defaultTitle,
  defaultDescription,
  routeDefaults,
  getRouteDefaults,
};
